import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import clsx from 'clsx';
import { useT } from '../../i18n';
import styles from './Toast.module.css';

export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
  duration: number;
}

interface ToastContextValue {
  show: (toast: Omit<ToastItem, 'id' | 'duration'> & { duration?: number }) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue['show']>((input) => {
    const id = nextIdRef.current++;
    const duration = input.duration ?? DEFAULT_DURATION_MS;
    setToasts((prev) => [...prev, { ...input, id, duration }]);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  const success = useCallback((title: string, description?: string) =>
    show({ kind: 'success', title, description }), [show]);
  const error = useCallback((title: string, description?: string) =>
    show({ kind: 'error', title, description }), [show]);
  const info = useCallback((title: string, description?: string) =>
    show({ kind: 'info', title, description }), [show]);

  const value = useMemo(
    () => ({ show, success, error, info }),
    [show, success, error, info],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.viewport} role="region" aria-live="polite" aria-label={t('components.toast.region')}>
        {toasts.map((t) => (
          <ToastView key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const { t } = useT();
  // Re-trigger enter animation on mount
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setEnter(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const Icon = toast.kind === 'success' ? CheckCircle2 : toast.kind === 'error' ? AlertCircle : Info;

  return (
    <div
      className={clsx(styles.toast, styles[`kind_${toast.kind}`], enter && styles.enter)}
      role={toast.kind === 'error' ? 'alert' : 'status'}
    >
      <Icon size={18} className={styles.icon} aria-hidden="true" />
      <div className={styles.body}>
        <div className={styles.title}>{toast.title}</div>
        {toast.description && <div className={styles.description}>{toast.description}</div>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className={styles.close}
        aria-label={t('components.toast.dismiss')}
      >
        <X size={16} />
      </button>
    </div>
  );
}
